/**
 * @file schemas/cases.ts
 * @description 驗證案件 Zod 校驗 schema（前後端共用，M1）。
 *
 * 提供以下 schema：
 * - createCaseSchema      建立案件請求校驗
 * - updateCaseSchema      更新案件請求校驗（所有欄位可選）
 * - dispatchCaseSchema    派案請求校驗（設置稽核團隊）
 * - addDocumentSchema     掛載單一案件文件請求校驗
 * - addDocumentsBatchSchema 批量掛載案件文件請求校驗
 */
import { z } from 'zod';
import { caseApplicationDetailsSchema } from './case-application';
/**
 * 驗證服務類型「內建預設清單」。
 *
 * ⚠️ 這**不是**唯一真相：實際可選項以後台字典表 `verification_types`
 *（/settings/verification-types，管理員可增刪維護）為準，前端下拉動態讀該字典。
 * 本常量僅作為兩種用途的回退 / 參考：
 *  1. 前端字典為空（種子未跑 / 全被停用）時的兜底選項，確保建案表單不至於無可選；
 *  2. {@link inferTaskType}（派工映射）與面積分流等**內建業務啟發式**的已知名對照。
 * 因此新增後台字典項不需要改這裡，但若要讓其被派工/面積邏輯正確識別，需同步對照表。
 */
export const VERIFICATION_TYPES = [
    '初次申請',
    '增列查驗',
    '追蹤查驗1',
    '追蹤查驗2',
    '追蹤查驗3',
    '展延查驗',
    '繼續驗證',
    '轉換驗證',
];
/**
 * 多選驗證服務類型的分隔符。
 *
 * 驗證服務類型由單值改為可複選後，仍相容既有 `cases.verification_type`（varchar(50)）
 * 單欄存儲：前端多選後以此分隔符拼成單一字串落庫，讀取端可用
 * {@link splitVerificationTypes} 拆回陣列。選用頓號「、」既是合法分隔，
 * 亦可直接作為中文枚舉展示（如「增列查驗、追蹤查驗1」）。
 */
export const VERIFICATION_TYPE_SEPARATOR = '、';
/** 將多選驗證服務類型陣列拼成存庫字串（保序、不額外去空白）。 */
export function joinVerificationTypes(types) {
    return types.join(VERIFICATION_TYPE_SEPARATOR);
}
/** 將存庫的驗證服務類型字串拆回陣列（空值回空陣列，去除空段與前後空白）。 */
export function splitVerificationTypes(value) {
    if (!value)
        return [];
    return value
        .split(VERIFICATION_TYPE_SEPARATOR)
        .map((s) => s.trim())
        .filter(Boolean);
}
/**
 * 驗證服務類型欄位 schema：相容單值與多選拼接字串。
 *
 * 可選值以後台字典 `verification_types`（管理員可維護）為準，故此處**不再**用內建
 * {@link VERIFICATION_TYPES} 白名單校驗具體值（shared 層無法讀 DB，硬校驗會誤拒
 * 管理員新增的類型），僅保留結構性約束：
 *  - 以 {@link VERIFICATION_TYPE_SEPARATOR} 連接的「一個或多個」非空項，至少一項；
 *  - 總長 ≤ 50 以對齊 DB 欄位 `cases.verification_type` varchar(50)。
 */
export const verificationTypeFieldSchema = z
    .string()
    .min(1, '請選擇驗證服務類型')
    .max(50, '驗證服務類型過多')
    .refine((v) => splitVerificationTypes(v).length > 0, {
    message: '請選擇驗證服務類型',
});
/**
 * 是否為「初次申請」類驗證服務類型。
 *
 * 後台字典可自訂名稱（種子值為「初次申請(包含轉證)」，存量資料另有「首次申請」寫法），
 * 故以「初次」/「首次」關鍵字判別，而非與 {@link VERIFICATION_TYPES} 硬比對。
 */
export function isInitialVerificationType(type) {
    return type.includes('初次') || type.includes('首次');
}
/**
 * 是否適用「預派案」（2026-09-11）。
 *
 * 業務背景：公司客戶多為老客戶，案件驗證服務類型多半不是「初次申請」（追蹤查驗 /
 * 展延 / 繼續 / 轉換…）。這類案件實務上先把案子派給稽核員（預派案），
 * 應附文件、合約評審、報價、繳費四步改為派案後由管理組補辦，不再作為派遣的前置關卡。
 *
 * 判定：驗證服務類型已填，且所有類型都不是「初次申請」類。複選時只要含一項初次申請
 * 就走原本的「文件→合約→報價→繳費→派遣」順序流程。
 *
 * @param verificationType 案件 `cases.verification_type` 原始值（可為多選拼接字串）
 */
export function isPreDispatchCase(verificationType) {
    const types = splitVerificationTypes(verificationType);
    if (types.length === 0)
        return false;
    return !types.some(isInitialVerificationType);
}
/**
 * 驗證資格「多選 → 單值回落」讀取工具（前後端共用）。
 *
 * 驗證資格 2026-07-04 起改為可多選（雙寫兼容，同 users.roles 模式）：
 * 新 jsonb 欄 `cases.verification_qualifications` 存全量陣列、
 * 舊 varchar 欄 `cases.verification_qualification` 存首值。
 * 讀取方統一用本函式：新欄有值取新欄，否則回落舊單值欄（舊資料仍可用），
 * 兩者皆空回空陣列。
 *
 * @param qualifications 新多選欄位原始值（jsonb 讀出為 unknown，容錯過濾非字串項）
 * @param single         舊單值欄位值
 * @returns              正規化後的驗證資格陣列（可能為空陣列）
 */
export function resolveVerificationQualifications(qualifications, single) {
    if (Array.isArray(qualifications)) {
        const arr = qualifications.filter((x) => typeof x === 'string' && x.trim() !== '');
        if (arr.length > 0)
            return arr;
    }
    return single ? [single] : [];
}
// ─── 申請表單明細 ─────────────────────────────────────────────────────────────
// 2026-07-04 起申請明細按驗證方案分流（TOP / TAP 判別聯合 + LEGACY 兼容），
// 全部定義遷至 ./case-application（applicationDetailsSchema / applicationDetailsTopSchema /
// applicationDetailsTapSchema / parseApplicationDetails / sumTapArea …），
// 由 @zhcb/shared index 統一導出；本檔僅引用寫入用聯合 schema。
/**
 * 稽核團隊成員請求結構。
 */
export const teamMemberInputSchema = z.object({
    /** 用戶 ID */
    userId: z.number().int().positive(),
    /** 成員角色（稽核組長/稽核員/觀察稽核員/見習稽核員/驗證助理） */
    role: z.string().min(1).max(50),
});
/**
 * 建立案件請求 schema。
 * caseCode 由後端 nextNumber() 自動生成，不由客戶端傳入。
 */
export const createCaseSchema = z.object({
    /** 客戶 ID */
    clientId: z.number().int().positive(),
    /** 驗證方案代碼 */
    scheme: z.enum(['TOP', 'TAP', 'ECO']),
    /** 驗證類型（單值或多選拼接字串，見 {@link verificationTypeFieldSchema}） */
    verificationType: verificationTypeFieldSchema,
    /** 驗證類別陣列（可選），如 ["農場", "加工場"] */
    verificationCategories: z.array(z.string()).optional(),
    /** 合約來源（自客戶下放至案件；預設「中華驗證」），值來自 contract_sources 字典 */
    contractSource: z.string().max(50).default('中華驗證'),
    /**
     * 驗證資格（主體類型，自客戶下放至案件），值來自 verification_qualifications 字典。
     * ⚠️ 已降級為單值相容欄位：新前端請改傳 {@link createCaseSchema.shape.verificationQualifications}
     * 多選陣列；服務層寫入時雙寫（舊欄存首值），舊調用方仍可只傳本欄。
     */
    verificationQualification: z.string().max(50).optional(),
    /**
     * 驗證資格多選陣列（2026-07-04 起，客戶 PPT S8：產銷履歷 TAP 需多選）。
     * 傳入時至少一項；服務層雙寫：新 jsonb 欄存全量陣列、舊欄 verificationQualification 存首值。
     */
    verificationQualifications: z.array(z.string().max(50)).min(1).optional(),
    /**
     * 申請表單明細，落庫於 cases.application_details（jsonb）。
     * 按驗證方案分流：TOP（kind='TOP'，ECO 暫按 TOP）/ TAP（kind='TAP'）/
     * 舊結構（無 kind，LEGACY 兼容）。kind 與 scheme 的一致性由服務層校驗
     * （applicationKindForScheme，錯配回 422 APPLICATION_KIND_MISMATCH）。
     */
    applicationDetails: caseApplicationDetailsSchema.optional(),
    /** 預期到期日（SLA 基準，ISO 8601 字串） */
    dueDate: z.string().datetime({ offset: true }).optional(),
    /** 備注 */
    notes: z.string().max(2000).optional(),
});
/**
 * 更新案件請求 schema（局部更新）。
 */
export const updateCaseSchema = createCaseSchema
    .pick({
    verificationType: true,
    verificationCategories: true,
    contractSource: true,
    verificationQualification: true,
    verificationQualifications: true,
    applicationDetails: true,
    dueDate: true,
    notes: true,
})
    .partial()
    .extend({
    /** null 表示明確清除到期日；undefined 表示不修改。 */
    dueDate: z.string().datetime({ offset: true }).optional().nullable(),
});
/**
 * 派案請求 schema。
 * 包含稽核組長 ID 以及完整的團隊成員列表（包含組長本人）。
 */
export const dispatchCaseSchema = z.object({
    /** 稽核組長用戶 ID */
    teamLeadId: z.number().int().positive(),
    /** 完整稽核團隊（含組長）；可只含組長一人 */
    members: z.array(teamMemberInputSchema).min(1).max(20),
    /**
     * 預期到期日（SLA 基準，ISO 8601 字串，可選）。
     * 依業務調整：到期日於「派遣」環節設定，不在建案時填寫；
     * 傳入時於派遣同事務一併寫入案件 dueDate。
     */
    dueDate: z.string().datetime({ offset: true }).optional().nullable(),
    /**
     * 稽核完成期限（2026-07-04 新增，客戶 PPT S47，ISO 8601 字串，可選）。
     * 管理組派案時指定「稽核完成時間：XXXX年XX月XX日前」；
     * 傳入時於派遣同事務寫入 cases.audit_deadline，並隨派案通知帶出期限文案。
     * 序105：兩個日期欄皆允許顯式傳 null 清除既有值（undefined＝保持原值）。
     */
    auditDeadline: z.string().datetime({ offset: true }).optional().nullable(),
    /** 派案備注 */
    reason: z.string().max(500).optional(),
    /**
     * 管理組備註（2026-07-04 新增，客戶 PPT S23）。
     * 記錄派案原因 / 改派說明 / 其他作業事項；落庫於 cases.admin_notes，
     * 並隨改派通知（case.dispatch.changed）模板 vars 帶出。
     */
    adminNotes: z.string().max(1000).optional(),
    /**
     * 驗證組備註（2026-07-04 新增，客戶 PPT S23）。
     * 記錄派案原因 / 改派說明 / 其他作業事項；落庫於 cases.verification_notes，
     * 並隨改派通知（case.dispatch.changed）模板 vars 帶出。
     */
    verificationNotes: z.string().max(1000).optional(),
});
/**
 * 派案團隊成員確認請求 schema。
 * 由稽核員/組長本人提交手寫簽名後調用，附 OSS files 表 ID。
 * 一旦確認不可重簽。
 */
export const confirmTeamMemberSchema = z.object({
    /** 簽名圖片（PNG）對應的 files 表 ID */
    signatureFileId: z.number().int().positive(),
});
/**
 * 掛載文件請求 schema。
 */
export const addDocumentSchema = z.object({
    /** OSS files 表 ID */
    fileId: z.number().int().positive(),
    /** 文件類型標籤 */
    docType: z.string().min(1).max(50),
    /** 備注 */
    note: z.string().max(500).optional(),
});
/**
 * 批量掛載案件文件請求 schema。
 *
 * 現場稽核「Z附件」允許一次選取多個補充證據；前端先批量直傳 OSS，再用此契約
 * 將已落庫的 files 記錄於同一事務內關聯到案件，避免逐筆關聯造成半套文件包。
 */
export const addDocumentsBatchSchema = z
    .object({
    /** OSS files 表 ID；與 files 批量 confirm 上限一致，單批最多 100 筆 */
    fileIds: z.array(z.number().int().positive()).min(1).max(100),
    /** 同一批附件共用的文件類型標籤（現場證據固定為「Z附件」） */
    docType: z.string().min(1).max(50),
    /** 同一批附件共用備注 */
    note: z.string().max(500).optional(),
})
    .refine((value) => new Set(value.fileIds).size === value.fileIds.length, {
    message: 'fileIds 不可重複',
    path: ['fileIds'],
});
/**
 * 從同一客戶歷史案件沿用文件。
 * 請求傳 case_documents 主鍵，而不是 files.id；後端據此校驗來源案件、文件類型和歸屬。
 */
export const reuseCaseDocumentsSchema = z
    .object({
    sourceDocumentIds: z.array(z.number().int().positive()).min(1).max(100),
})
    .refine((value) => new Set(value.sourceDocumentIds).size === value.sourceDocumentIds.length, {
    message: 'sourceDocumentIds 不可重複',
    path: ['sourceDocumentIds'],
});
/**
 * 更新案件文件關聯請求 schema（僅允許修改 docType / note，不允許更換 fileId）。
 */
export const updateCaseDocumentSchema = z.object({
    /** 文件類型標籤（不可為空字串） */
    docType: z.string().min(1).max(50).optional(),
    /** 備注（傳 null 可清空；傳 undefined 表示不修改） */
    note: z.string().max(500).nullable().optional(),
    /**
     * 替換檔案（序66.4：應附文件修改後上傳覆蓋原表單）——
     * 傳入新上傳的 file id，原檔案關聯被替換（files 記錄保留供追溯）。
     */
    fileId: z.number().int().positive().optional(),
});
